import React from 'react'

import { whatsappChatProps } from '../../../components/whatsapp-chat'
import { chargesOnCompletion, payLead, payDetail } from '../../../lib/payment-copy'
import { cutoffLabel } from '../../../lib/same-day'
import { goodToKnow, splitAnswer } from '../../../lib/good-to-know'

// THE CONTENT OF /faqs. Three groups, each one a <Section> with its own H2
// (./index.js paints them), each question a <details> whose <summary> is the
// H3.
//
// EVERY ANSWER HERE IS SOURCED. The page this replaced had fourteen headings
// and several of them were no longer true, which is worse than having no FAQ
// at all: a customer who finds one answer wrong stops believing the other
// thirteen. So the rule for this file is the one sections/how-it-works works
// to: a claim goes in only with something in this repository behind it, and
// where the sources disagree the page says less rather than picking one.
//
// WHAT WAS CORRECTED, and against what:
//
//   "You will receive a confirmation email once your car has been washed"
//     OrdersEvents#on_complete calls notify_customer('completed'), which is
//     ONE channel and not two (docs/DECISIONS.md 2026-09-28): WhatsApp
//     (wash_completed_v2, photo header, then paced wash_completion_photo
//     sends) when the customer is reachable there, and the order_completed
//     email only when they are not. Promising everybody an email was wrong
//     for most customers.
//
//   "one is automatically sent to you in a separate email after the
//   completion of your car wash" (the receipt)
//     There is no receipt mailer in app/mailers at all. What actually
//     arrives is Stripe's own card receipt, because
//     User::Stripe#process_stripe_payment passes `receipt_email`, and that
//     PaymentIntent is created by Order::AcceptService. So the receipt lands
//     when a washer ACCEPTS, not when the wash is finished.
//
//   "Enter your postcode on the homepage to check your street"
//     There is no postcode field on the home page any more and DESIGN.md §3
//     bans the postcode gate by name. The areas index
//     (sections/areas-cta) is what replaced it.
//
//   the booking steps
//     Rewritten against pages/booking/components/step-book: the wash picker
//     (three cards, the rest behind "See all washes"), car size plus the
//     Congestion Charge Zone checkbox, date and slot, the "Anything we
//     should know?" disclosure, the extras chips, then CheckoutPanel. There
//     is no map and no pin: docs/DECISIONS.md 2026-10-04, "The booking form
//     asks for a postcode and an address, and nothing is geocoded". The card
//     path collects a UK mobile here and the name, email and card on
//     Stripe's own page (step-checkout.js: "Name + email are collected by
//     Stripe Checkout").
//
//   "contact your Operator"
//     Impossible since assignment went internal (OrdersEvents#on_accept,
//     2026-09-29). Every route out of this page is now WhatsApp or
//     support@gowashmycar.com, which is what the header, the footer and
//     sections/how-it-works already do.
//
//   the services
//     No wash is named and no price is quoted anywhere on this page. The six
//     live washes and the add-ons are on /services, read from the
//     gwmc:prices meta and the services API; the old copy named services
//     that have since been retired. A price in this file would go stale and
//     nobody would notice.
//
// WHAT THIS PAGE DELIBERATELY DOES NOT SAY, because the sources contradict
// each other and only Robin can settle it. Both are in the build report:
//
//   A CANCELLATION NOTICE PERIOD OR FEE. The old copy said free until 2
//   hours before the window, then 50% of the fee. The terms page says
//   amendments up to 24 hours, a 50% REFUND inside 24 hours (the opposite
//   meaning) and no refund at all inside 4 hours. Order::CancelService
//   applies no time test and no fee whatsoever and issues full credit.
//   Three sources, three policies, and the code is the most generous. So the
//   answer below says the one thing all three agree on, which is that a
//   person deals with it by hand, and commits to saying what is due before
//   any money moves.
//
//   A DISTANCE IN METRES FOR THE VAN. The old copy and SeoHelper::FAQ say
//   about 10 metres; app/views/shared/_booking_notice.html.haml, which goes
//   out with every booking confirmation, says 20. The terms give no number
//   at all and say instead that the van parks directly in front of or behind
//   the car. That last one is the only version nothing contradicts, so it is
//   what the answer says.
//
//   And no time to acceptance, for the reason sections/how-it-works sets out
//   at length: there is no accept-latency metric in this codebase, and a
//   time on a page is a promise an operator has to keep.
//
// NOR THE WORD "ACCEPT", ANYWHERE A CUSTOMER CAN READ IT (5 Oct). Three
// answers here used to explain our dispatch: the card was charged "at the
// moment a washer accepts the job", nothing was taken "while we are still
// finding one", "if nobody accepts the slot you are not charged at all", and
// one whole question was "What if nobody can take my slot?". Each was true.
// Together they described a confirmed booking as a search in progress, and
// the one that mattered ended on the failure case, which is the sentence a
// reader remembers. Robin, 5 Oct: "we dont want anyone thinking their
// booking is shakey.. it's a massive turnoff".
//
// What replaced them: one answer, in Robin's own wording, saying that the
// card is charged shortly after the booking is made and that we keep it on
// file to make repeat bookings easy. No trigger is named. "What if nobody
// can take my slot?" is DELETED rather than reworded: its only content was
// the forbidden line, and "I need to move or cancel my booking." already
// routes the reader to a person. Who takes the job is internal
// (OrdersEvents#on_accept, 2026-09-29) and a customer has no use for it.
//
// The exception path is real and is handled by people: if a booking cannot
// be covered, support contacts the customer (on_cancel(:admin) is the only
// path that messages them). It is not written down on any page, because
// stating it cost far more in doubt than it bought in reassurance.

const SUPPORT_EMAIL = 'support@gowashmycar.com'

const SupportEmail = () => (
  <a className="gwmc-link" href={`mailto:${SUPPORT_EMAIL}`}>
    {SUPPORT_EMAIL}
  </a>
)

// Every WhatsApp link on the site goes through whatsappChatProps, which
// keeps the wa.me href on a phone and opens the number form on a desktop
// instead (docs/WHATSAPP.md). A bare wa.me link on a laptop is the dead end
// that fix exists for.
const WhatsAppLink = ({ text, children }) => (
  <a className="gwmc-link" {...whatsappChatProps(text)}>
    {children}
  </a>
)

// THE 5 OCT 2026 REWRITE (copy deck, approved by Robin). Every answer leads
// with what the customer gets and stops; process and disclaimers are gone.
// The facts about the valeters (five years' experience minimum, trained by
// Barry Cohen, ID / right to work / references, public liability insurance,
// microfibre) were confirmed by Robin that day. 25 Oct 2026 (Barry, via
// Robin: "he doesnt want to go into too much detail on the type of water
// used, maybe scale back the pH-neutral side"): the water type and pH are no
// longer claimed anywhere a customer reads. "Professional products" and "a
// careful hand wash" say it instead, and snow foam is "where the spot allows".
// 2 NOV 2026 (Robin: "we're not doing two bucket method"): no method claim of
// any kind. Out of these answers went "fresh microfibre on every car", "with
// professional products and microfibre" and "with soft microfibre"; what is
// left is what can be sourced. No bucket counts, no grit guards, no drying
// technique, no microfibre specifics, and clay bar only as the paid extra.
// app/helpers/seo_helper.rb FAQ carries every question and answer below, word for word, for the FAQPage JSON-LD: change one, change both.
//
// 23 OCT 2026 (source: Barry's suggested scripts, edited by the PM; Robin:
// "the new language should just be reflected in all versions"). Four
// answers now carry the same words as the home page's how-it-works answers,
// the wash pages, the booking confirmation's notice and the coach: "Is the
// price fixed?" (was "What does a wash cost?"; the services link now sits
// on "price you see", and it adds that anything extra on the day is agreed
// with you first), "Do I need to be home?" (was "...be there?"; names the
// ways into the car for an interior), "Where does the car need to be?"
// (safe and legal, space to open the doors) and "What if it rains?" (most
// go ahead; move it before the valeter sets off). Still no cancellation
// fee or notice period and no distance in metres.
// 26 OCT 2026: the price answer is Robin's wording, verbatim ("The price
// quoted is based on the service you have selected. ..."); the services link
// moved to "price quoted".
//
// 25 OCT 2026: those four are no longer typed here. They are
// seo/good_to_know.json (lib/good-to-know.js), the one copy the home page,
// SeoHelper::FAQ and every wash page's Good to know block also read.
const shared = id => {
  const item = goodToKnow(id)
  const [before, linkText, after] = splitAnswer(item)
  let link = null
  if (linkText && item.link.whatsapp) {
    link = <WhatsAppLink text={item.link.whatsapp}>{linkText}</WhatsAppLink>
  } else if (linkText) {
    link = (
      <a className="gwmc-link" href={item.link.href}>
        {linkText}
      </a>
    )
  }
  return {
    q: item.q,
    a: (
      <p className="faq-p">
        {before}
        {link}
        {after}
      </p>
    )
  }
}

export const FAQ_GROUPS = [
  {
    id: 'booking',
    band: 'paper',
    title: 'Booking a wash',
    intro: 'Two minutes, one screen, no account.',
    items: [
      {
        q: 'Which parts of London do you cover?',
        a: (
          <p className="faq-p">
            Every London borough, plus the nearby parts of Hertfordshire, Essex, Kent and Surrey. Not
            sure?{' '}
            <WhatsAppLink text="Hi GoWashMyCar! Do you cover my postcode?">
              Send us your postcode on WhatsApp.
            </WhatsAppLink>
          </p>
        )
      },
      {
        q: 'How do I book?',
        a: (
          <p className="faq-p">
            It takes about two minutes and there’s no account to create. Pick your wash, your car size, a
            day and a time, add any extras, tell us where the car is, then pay with Apple Pay, Google Pay
            or card. You can add a 10% tip for your valeter at checkout.
          </p>
        )
      },
      {
        q: 'Can I book two cars?',
        a: (
          <p className="faq-p">
            Yes. Book each car as its own booking, at the same address and time, and mention the other car in
            the notes for your valeter.
          </p>
        )
      },
      shared('price'),
      {
        q: 'Do I need an account?',
        a: <p className="faq-p">No. Just a mobile number and email so we can confirm your booking.</p>
      },
      {
        q: 'When can a wash actually happen?',
        a: (
          <p className="faq-p">
            You can book at any time of day. Washes run every day from 8am to 6pm, and you can book today’s
            wash until {cutoffLabel()}.
          </p>
        )
      },
      {
        q: 'Do you do detailing, paint restoration, tar or concrete removal?',
        a: (
          <p className="faq-p">
            Yes.{' '}
            <WhatsAppLink text="Hi GoWashMyCar! I'd like to ask about a detailing job">
              Send us a photo on WhatsApp
            </WhatsAppLink>{' '}
            for a quick quote.
          </p>
        )
      }
    ]
  },
  {
    id: 'on-the-day',
    band: 'stone',
    title: 'On the day',
    intro: 'Who comes, what they bring, and what you need to do (nothing).',
    items: [
      shared('where'),
      shared('rain'),
      {
        q: 'Who will wash my car?',
        a: (
          <p className="faq-p">
            A professional valeter with at least five years’ experience, trained by our head of
            operations, Barry Cohen, who has been valeting cars in London since 1999. Every valeter is ID
            checked, right-to-work checked and referenced, and fully insured with public liability cover.
            They arrive in a fully equipped van and wash your car by hand, so it gets a showroom
            finish without a drop of water or power from you.
          </p>
        )
      },
      // 4 NOV 2026 (Barry via Robin: "lets not embellish the type of wash"):
      // this answer named snow foam as a stage of the wash. It now answers
      // with what turns up and what the customer gets. Word for word as
      // SeoHelper::FAQ states it in the FAQPage: change one, change both.
      {
        q: 'What do you use on my car?',
        a: (
          <p className="faq-p">
            Everything the wash needs comes in the van, its own water and power included, so nothing is
            needed from your house. Your valeter has at least five years in the trade and is fully
            insured, and at the end you get photographs of the finished car.
          </p>
        )
      },
      // The EV and leather answers, 6 Oct 2026 (the wash-pages content, plan
      // item 9). Barry's product facts in them are provisional placeholders
      // Robin approved ("make this up for the mo").
      {
        q: 'Is it safe for my electric or hybrid car?',
        a: (
          <p className="faq-p">
            Yes. Every wash is done by hand, the charge port stays closed throughout, and the rinse is gentle and never aimed at the charge port, seams or
            sensors. Just make sure the car isn’t on charge when your valeter arrives.
          </p>
        )
      },
      {
        q: 'Is it safe on leather seats?',
        a: (
          <p className="faq-p">
            Yes. Leather is cleaned with a dedicated leather cleaner and a soft brush, then conditioned to
            keep it soft and supple. It’s included in the Full Valet, the Interior Only Valet and the Dog
            Owners Valet, and you can add it to any other wash as Deep clean, two seats.
          </p>
        )
      },
      {
        q: 'What about child seats and anything else clipped in?',
        a: <p className="faq-p">Take them out before we arrive and we’ll clean underneath too.</p>
      },
      {
        q: 'How will I know the wash is done?',
        a: (
          <p className="faq-p">
            You get photos of your freshly washed car on WhatsApp or email as soon as your valeter
            finishes.
          </p>
        )
      },
      // Last in the group: Robin, 25 Oct 2026, "this q&a is def least
      // important". Off the home page and the wash pages (hidden_on).
      shared('be_home')
    ]
  },
  {
    id: 'paying',
    band: 'paper',
    title: 'Paying, changing and cancelling',
    intro: 'Simple, secure payment.',
    items: [
      {
        q: 'When do I pay?',
        a: (
          <>
            {/* Same words as SeoHelper.faq, which reads the same switch. */}
            <p className="faq-p">
              {chargesOnCompletion()
                ? `${payLead()} ${payDetail()} Cancel free any time before your slot.`
                : 'Your card is charged shortly after your booking is made. We keep it on file to make repeat bookings easy, and Stripe holds it, so we never see or store the details.'}
            </p>
            <p className="faq-p">You’ll hear from us on the day, when your valeter is on the way.</p>
          </>
        )
      },
      {
        q: 'Where are my card details kept?',
        a: (
          <p className="faq-p">
            With Stripe. We keep it on file to make repeat bookings easy, and we never see or store the
            number: all we hold is the last four digits and the expiry date, so you can tell which card it
            was.
          </p>
        )
      },
      {
        q: 'Can I have a receipt so I can expense the wash?',
        a: (
          <p className="faq-p">
            Stripe emails you a card receipt when the payment goes through. If you need something with
            more on it than that, email <SupportEmail /> and we will sort it out.
          </p>
        )
      },
      {
        q: 'I need to move or cancel my booking.',
        a: (
          <p className="faq-p">
            {chargesOnCompletion()
              ? 'Cancel free any time before your slot starts. Your card is only charged once the wash is done.'
              : 'Cancel free any time before your slot starts, and we refund your card in full.'}{' '}
            Just message us on{' '}
            <WhatsAppLink text="Hi GoWashMyCar! I need to change a booking">WhatsApp</WhatsAppLink> or email{' '}
            <SupportEmail /> and we’ll sort it.
          </p>
        )
      }
    ]
  }
]
